import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { primaryCashPaymentFromSale } from '@/utils/cashPaymentDisplay'

function contactHintsFromSale (sale) {
  if (!sale || typeof sale !== 'object') {
    return { customerId: null, prefilledEmail: '', prefilledPhone: '' }
  }
  const customerId = sale.customer_id ?? sale.customer?.id ?? null
  const prefilledEmail =
    sale.customer?.primary_email?.email ||
    sale.customer?.email ||
    ''
  const prefilledPhone =
    sale.customer?.primary_phone?.phone ||
    sale.customer?.phone ||
    ''
  return { customerId, prefilledEmail, prefilledPhone }
}


export const usePostSaleReceiptStore = defineStore('postSaleReceipt', () => {
  const pendingSaleId = ref(null)
  const pendingSaleSnapshot = ref(null)
  const customerId = ref(null)
  const prefilledEmail = ref('')
  const prefilledPhone = ref('')
  const tipTotal = ref(0)
  const status = ref('idle')
  const showCashierChoices = ref(false)
  const lastError = ref('')

  const showCashChange = ref(false)
  const amountReceived = ref(0)
  const changeGiven = ref(0)

  const isPending = computed(() => status.value === 'pending' || status.value === 'processing')
  const hasPendingSale = computed(() => pendingSaleId.value != null)

  function applyCashChangeFromSale (sale) {
    const cash = primaryCashPaymentFromSale(sale)
    if (!cash) {
      showCashChange.value = false
      amountReceived.value = 0
      changeGiven.value = 0
      return
    }

    showCashChange.value = true
    amountReceived.value = Number(cash.amount_received) || 0
    changeGiven.value = Number(cash.change_given ?? 0)
  }

  function setPendingFromSale (sale) {
    const id = sale?.id ?? sale?.sale_id
    if (!id) return
    const hints = contactHintsFromSale(sale)
    if (pendingSaleId.value != null && String(pendingSaleId.value) !== String(id)) {
      lastError.value = ''
    }
    pendingSaleId.value = id
    pendingSaleSnapshot.value = sale ? { ...sale } : null
    customerId.value = hints.customerId
    prefilledEmail.value = hints.prefilledEmail
    prefilledPhone.value = hints.prefilledPhone
    tipTotal.value = Math.max(0, Number(sale?.tip_total || 0))
    status.value = 'pending'
    lastError.value = ''
    applyCashChangeFromSale(sale)
    showCashierChoices.value = true
  }

  function markProcessing () {
    status.value = 'processing'
    lastError.value = ''
  }

  function markDone () {
    status.value = 'done'
    lastError.value = ''
    showCashierChoices.value = false
    pendingSaleId.value = null
    pendingSaleSnapshot.value = null
    customerId.value = null
    prefilledEmail.value = ''
    prefilledPhone.value = ''
    tipTotal.value = 0
    showCashChange.value = false
    amountReceived.value = 0
    changeGiven.value = 0
  }

  function clearPending () {
    status.value = 'idle'
    lastError.value = ''
    showCashierChoices.value = false
    pendingSaleId.value = null
    pendingSaleSnapshot.value = null
    customerId.value = null
    prefilledEmail.value = ''
    prefilledPhone.value = ''
    tipTotal.value = 0
    showCashChange.value = false
    amountReceived.value = 0
    changeGiven.value = 0
  }

  function setError (message) {
    lastError.value = message || ''
    status.value = 'pending'
  }

  function markReceiptComplete () {
    markDone()
    return true
  }

  function paymentCompletePayload () {
    return {
      saleId: pendingSaleId.value,
      customerId: customerId.value,
      prefilledEmail: prefilledEmail.value || '',
      prefilledPhone: prefilledPhone.value || ''
    }
  }

  return {
    pendingSaleId,
    pendingSaleSnapshot,
    customerId,
    prefilledEmail,
    prefilledPhone,
    tipTotal,
    status,
    showCashierChoices,
    lastError,
    showCashChange,
    amountReceived,
    changeGiven,
    isPending,
    hasPendingSale,
    setPendingFromSale,
    markProcessing,
    markDone,
    clearPending,
    setError,
    markReceiptComplete,
    paymentCompletePayload,
    contactHintsFromSale
  }
})
